import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useRef, useState, useEffect, useMemo, Suspense } from 'react'

import type {
  ClientType,
  LoanType,
  CreditScore,
  TitleTransactionType,
  AttorneyTransactionType,
  ProjectType,
  AppraiserTransactionType,
} from 'src/shared/schemas/referrals'
import {
  appraiserTransactionTypeMap,
  attorneyTransactionTypeMap,
  clientTypeMap,
  titleTransactionTypeMap,
  creditScoreMap,
  flatLoanTypeMap,
  projectTypeMap,
  propertyTypeMap,
} from 'src/shared/schemas/referrals'
import { abbreviateMoney } from 'src/shared/utils/abbreviate-money'
import { formatMessageTime } from 'src/shared/utils/dates/format-message-time'

import type { Transaction } from '../../../api/marketplace/use-user-transactions'
import { Bath } from '../../../assets/transactions/bath'
import { Bed } from '../../../assets/transactions/bed'
import { Credit } from '../../../assets/transactions/credit'
import { House } from '../../../assets/transactions/house'
import { Legal } from '../../../assets/transactions/legal'
import { MoneyBag } from '../../../assets/transactions/money-bag'
import { MoneyTag } from '../../../assets/transactions/money-tag'
import { Shield } from '../../../assets/transactions/shield'

import TransactionCardSkeleton from './transaction-card-skeleton'

const InfoCardMap = {
  loanType: {
    Icon: () => <House className="size-12" />,
    label: 'Loan type',
    getValue: (value: LoanType) => flatLoanTypeMap[value].label,
  },
  clientType: {
    Icon: () => <House className="size-12" />,
    label: 'Type',
    getValue: (value: ClientType) => clientTypeMap[value].label,
  },
  exactPrice: {
    Icon: () => <MoneyTag className="size-12" />,
    label: 'Purchase price',
    getValue: (value: number) => abbreviateMoney(value, 1),
  },
  downPaymentEstimate: {
    Icon: () => <MoneyBag className="size-12" />,
    label: 'Down payment',
    getValue: (value: number) => abbreviateMoney(value, 1),
  },
  estimatedPropertyValue: {
    Icon: () => <MoneyTag className="size-12" />,
    label: 'Property value',
    getValue: (value: number) => abbreviateMoney(value, 1),
  },
  creditScoreEstimate: {
    Icon: () => <Credit className="size-12" />,
    label: 'Credit score',
    getValue: (value: CreditScore) => creditScoreMap[value].label,
  },
  budget: {
    Icon: () => <MoneyTag className="size-12" />,
    label: 'Budget',
    getValue: (value: { min: number; max: number }) =>
      `${abbreviateMoney(value.min, 1)}-${abbreviateMoney(value.max, 1)}`,
  },
  priceRange: {
    Icon: () => <MoneyTag className="size-12" />,
    label: 'Price range',
    getValue: (value: { min: number; max: number }) =>
      `${abbreviateMoney(value.min, 1)}-${abbreviateMoney(value.max, 1)}`,
  },
  bedrooms: {
    Icon: () => <Bed className="size-12" />,
    label: 'Bedrooms',
    getValue: (value: number) => (Number.isInteger(value) ? value.toString() : value.toFixed(1)),
  },
  bathrooms: {
    Icon: () => <Bath className="size-12" />,
    label: 'Bathrooms',
    getValue: (value: number) => (Number.isInteger(value) ? value.toString() : value.toFixed(1)),
  },
  titleTransactionType: {
    Icon: () => <House className="size-12" />,
    label: 'Transaction type',
    getValue: (value: TitleTransactionType) => titleTransactionTypeMap[value].label,
  },
  appraisalPurpose: {
    Icon: () => <House className="size-12" />,
    label: 'Appraisal purpose',
    getValue: (value: AppraiserTransactionType) => appraiserTransactionTypeMap[value].label,
  },
  projectType: {
    Icon: () => <House className="size-12" />,
    label: 'Project type',
    getValue: (value: ProjectType) => projectTypeMap[value].label,
  },
  attorneyTransactionType: {
    Icon: () => <Legal className="size-12" />,
    label: 'Transaction type',
    getValue: (value: AttorneyTransactionType) => attorneyTransactionTypeMap[value].label,
  },
  coverage: {
    Icon: () => <Shield className="size-12" />,
    label: 'Coverage',
    getValue: (value: string[] | null | undefined) => {
      if (!value?.length) return ''
      if (value.length <= 2) return value.join(', ')
      return `${value[0]}, ${value[1]} +${value.length - 2}`
    },
  },
}

const InfoCard = ({ field, value }: { field: string; value: any }) => {
  if (!value || !InfoCardMap[field]) return null

  const { Icon, label, getValue } = InfoCardMap[field]

  return (
    <div className="flex flex-row gap-4 rounded-md border px-4 py-3 md:gap-6 md:py-4">
      <Icon />
      <div className="flex flex-col">
        <p className="whitespace-nowrap text-xs font-medium text-muted md:text-sm">{label}</p>
        <p className="whitespace-nowrap text-lg font-extrabold md:text-2xl">{getValue(value)}</p>
      </div>
    </div>
  )
}

const TransactionCardContent = ({ transaction }: { transaction: Transaction }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setShowLeftArrow(scrollLeft > 0)
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth)
    }
  }

  useEffect(() => {
    checkScroll()
  }, [])

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  const title = useMemo(() => {
    if (!transaction.propertyType) return `Property in ${transaction.propertyCity}, ${transaction.propertyState}`

    return `${propertyTypeMap[transaction.propertyType].label} in ${transaction.propertyCity}, ${
      transaction.propertyState
    }`
  }, [transaction])

  return (
    <div className="relative flex h-auto w-full max-w-full shrink-0 flex-col border-t py-4 md:py-8">
      <h2 className="text-lg font-bold leading-relaxed md:text-2xl">{title}</h2>
      <p className="text-sm text-muted md:text-base">{formatMessageTime(transaction.updatedAt)}</p>
      <div className="group/transaction-card relative w-full">
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-3 opacity-0 shadow-lg transition-opacity duration-200 group-hover/transaction-card:opacity-100 hover:bg-white"
          >
            <ChevronLeft className="size-8 text-primary" />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          onScroll={checkScroll}
          className="mt-4 flex w-full flex-row gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <InfoCard field="loanType" value={transaction.loanType} />
          <InfoCard field="clientType" value={transaction.clientType} />
          <InfoCard field="exactPrice" value={transaction.exactPrice} />
          <InfoCard field="budget" value={transaction.budget} />
          <InfoCard field="priceRange" value={transaction.priceRange} />
          <InfoCard field="creditScoreEstimate" value={transaction.creditScoreEstimate} />
          <InfoCard field="downPaymentEstimate" value={transaction.downPaymentEstimate} />
          <InfoCard field="estimatedPropertyValue" value={transaction.estimatedPropertyValue} />
          <InfoCard field="bedrooms" value={transaction.bedrooms} />
          <InfoCard field="bathrooms" value={transaction.bathrooms} />
          <InfoCard field="titleTransactionType" value={transaction.titleTransactionType} />
          <InfoCard field="appraisalPurpose" value={transaction.appraisalPurpose} />
          <InfoCard field="projectType" value={transaction.projectType} />
          <InfoCard field="attorneyTransactionType" value={transaction.attorneyTransactionType} />
          <InfoCard field="coverage" value={transaction.coverage} />
        </div>

        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 opacity-0 shadow-lg transition-opacity duration-200 group-hover/transaction-card:opacity-100 hover:bg-white"
          >
            <ChevronRight className="size-8 text-primary" />
          </button>
        )}
      </div>
    </div>
  )
}

const TransactionCard = ({ transaction }: { transaction: Transaction }) => {
  return (
    <Suspense fallback={<TransactionCardSkeleton />}>
      <TransactionCardContent transaction={transaction} />
    </Suspense>
  )
}

export default TransactionCard
